import { useEffect, useState } from 'react';
import { ptBR } from '@/i18n/pt-BR';
import { useApiClient } from '@/lib/api';
import {
  abreviarCodigo,
  formatarRecebimento,
  getCadeiaCustodia,
  type CadeiaCustodiaData,
  type PecaCustodiada,
  type TipoPeca,
} from '@/lib/cadeia_custodia';
import { DocumentosRegistradosTabela } from '@/components/DocumentosRegistradosTabela';
import {
  Blueprint,
  CardKicker,
  MonoData,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui';

const t = ptBR.cadeiaCustodia;

// Espelha `_TIPO_PECA_PT_BR` do Apêndice B: um tipo novo no backend precisa
// entrar aqui, ou o TypeScript acusa o `Record` incompleto (E82.F7.T03 — a
// foto do signatário saía rotulada como "Assinatura").
const ROTULO_DO_TIPO: Record<TipoPeca, string> = {
  documento: t.tipoDocumento,
  assinatura: t.tipoAssinatura,
  foto: t.tipoFoto,
};

function Pecas({ pecas }: { pecas: PecaCustodiada[] }): JSX.Element {
  if (pecas.length === 0) {
    return (
      <p className="mt-sc-4 text-body-sm text-fg-muted" data-testid="cadeia-custodia-vazia">
        {t.vazia}
      </p>
    );
  }
  return (
    <div className="mt-sc-4 overflow-x-auto">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>{t.colPeca}</TableHead>
            <TableHead>{t.colAutor}</TableHead>
            <TableHead>{t.colRecebida}</TableHead>
            <TableHead>{t.colCodigo}</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {pecas.map((peca) => (
            <TableRow key={peca.id} data-testid="cadeia-custodia-linha">
              <TableCell>
                <span className="text-caption uppercase tracking-wide text-fg-muted">
                  {ROTULO_DO_TIPO[peca.tipo]}
                </span>
                <span className="block text-body-sm text-fg">{peca.descricao}</span>
              </TableCell>
              <TableCell className="text-body-sm text-fg">{peca.anexado_por}</TableCell>
              <TableCell className="text-body-sm text-fg">
                {formatarRecebimento(peca.anexado_em)}
              </TableCell>
              <TableCell>
                {peca.sha256 === null ? (
                  <span className="text-body-sm text-fg-muted">{t.semCodigo}</span>
                ) : (
                  <MonoData
                    className="text-body-sm text-fg"
                    title={peca.sha256}
                    data-testid="cadeia-custodia-codigo"
                  >
                    {abreviarCodigo(peca.sha256)}
                  </MonoData>
                )}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}

interface CadeiaCustodiaPanelProps {
  caseId: string;
}

/**
 * Cadeia de custódia na tela do laudo (E58.F2 — ADR-0067). // ux-neutrality:allow doc-comment
 *
 * Espelha o Apêndice B do documento gerado, da mesma fonte. Peça sem código de // ux-neutrality:allow doc-comment
 * verificação aparece DECLARADA como anterior ao registro — esconder a linha // ux-neutrality:allow doc-comment
 * daria a impressão de que a peça não existiu. // ux-neutrality:allow doc-comment
 */
export function CadeiaCustodiaPanel({ caseId }: CadeiaCustodiaPanelProps) {
  const client = useApiClient();
  const [dados, setDados] = useState<CadeiaCustodiaData | null>(null);
  const [erro, setErro] = useState(false);

  useEffect(() => {
    let cancelado = false;
    setDados(null);
    setErro(false);
    void getCadeiaCustodia(client, caseId)
      .then((resposta) => {
        if (!cancelado) setDados(resposta);
      })
      .catch(() => {
        if (!cancelado) setErro(true);
      });
    return () => {
      cancelado = true;
    };
  }, [caseId, client]);

  // Um nome para "carregou sem erro": os três blocos abaixo dependem dele, e o
  // TypeScript estreita `dados` a partir da própria condição.
  const pronto = !erro && dados !== null;

  return (
    <Blueprint as="section" data-testid="cadeia-custodia-panel" className="p-sc-4">
      <CardKicker className="block">{t.kicker}</CardKicker>
      <h2 className="mt-sc-2 text-heading-md font-semibold text-fg">{t.titulo}</h2>
      <p className="mt-sc-2 max-w-3xl text-body-sm text-fg-muted">{t.descricao}</p>

      {erro && (
        <p role="alert" className="mt-sc-4 bg-danger-50 px-4 py-3 text-body-sm text-danger">
          {t.erroCarregar}
        </p>
      )}

      {!erro && dados === null && <Skeleton className="mt-sc-4 h-24 w-full" />}

      {pronto && (
        <>
          <Pecas pecas={dados.pecas} />
          {/* E85.F2 (GUS-32): a segunda lista do registro — o que o sistema
              produziu. Resposta anterior ao campo conta como lista vazia. */}
          <DocumentosRegistradosTabela documentos={dados.documentos ?? []} />
        </>
      )}

      <p className="mt-sc-4 max-w-3xl text-caption text-fg-muted">{t.limite}</p>
    </Blueprint>
  );
}
